"use client";

import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { isValidElement, useState } from "react";
import X from "@/assets/icons/x.svg";
import ClassicLoader from "@/components/common/classic-loader";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { getProductRating } from "@/features/rating/api/get-product-rating";
import { RatingDetails } from "./rating-details";

type Props = {
  productId: string;
  productName?: string;
  merchantName?: string;
  trigger?: React.ReactNode;
  /**
   * Set when the trigger already renders its own interactive element, so the
   * dialog does not wrap it in a second button.
   */
  triggerAsChild?: boolean;
};

const useProductRating = (productId: string, enabled?: boolean) => {
  return useQuery({
    queryKey: ["product-rating", productId],
    queryFn: () => getProductRating(productId),
    enabled,
    retry: false,
  });
};

export const RatingsDialog = ({
  productId,
  trigger,
  productName,
  merchantName,
  triggerAsChild,
}: Props) => {
  const [open, setOpen] = useState(false);
  const t = useTranslations("rating");
  const {
    data: rating,
    isPending,
    isError,
  } = useProductRating(productId, open);

  const loading = open && isPending;
  const isEmpty = !(loading || isError || rating);

  return (
    <Dialog onOpenChange={setOpen} open={open}>
      <DialogTrigger asChild={triggerAsChild ?? !isValidElement(trigger)}>
        {trigger}
      </DialogTrigger>
      <DialogContent
        className="flex max-h-[calc(100dvh-2rem)] flex-col gap-0 overflow-hidden p-0 max-sm:fixed max-sm:inset-0 max-sm:h-full max-sm:max-h-[100dvh] max-sm:max-w-full max-sm:translate-x-0 max-sm:translate-y-0 max-sm:rounded-none sm:h-[864px] sm:w-[864px] sm:max-w-[864px]"
        closeButton={false}
      >
        <DialogHeader className="flex h-fit flex-row items-center justify-between border-b px-6 py-4">
          <DialogTitle className="font-medium text-xl">
            {productName ?? t("dialogTitle")}
          </DialogTitle>
          <DialogClose aria-label={t("close")}>
            <X className="h-6 w-6" />
          </DialogClose>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 py-6 sm:overflow-hidden md:px-6 md:py-4">
          {loading && (
            <div className="flex h-16 items-center justify-center">
              <ClassicLoader />
            </div>
          )}
          {isError && <p className="text-brand-green2">{t("error")}</p>}
          {rating && !isError && (
            <RatingDetails
              merchantName={merchantName}
              productId={productId}
              rating={rating}
            />
          )}
          {isEmpty && (
            <div className="flex flex-col items-center gap-y-2 py-10 text-center">
              <p className="font-medium">{t("emptyTitle")}</p>
              <p className="text-brand-green2 text-sm">{t("emptySubtitle")}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
